import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AdTable from './AdsTable.js';
import { useTableState } from '../utils/useTableState.js';
import { columnsConfig } from '../MyAds/tableConfig.js';
import FilterBar from './FilterBar.js';
import { usePersistedFilterState } from '../utils/usePersistedFilterState.js';
 const types = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "full_page",
    label: "Full Image",
  },
  {
    value: "full_video",
    label: "Full Video",
  },
  {
    value: "promoted_image_story",
    label: "Promoted Image Story ",
  },
    {
        value:"promoted_video_story",
        label:"Promoted Video Story"
    },
    {
        value:"half_video",
        label:"Half Video"
    },
    {
    value: "classifiedads",
    label: "Classified Ads"
  }
]
// Status options from cellRenderers.js
const STATUS_OPTIONS = [
    { value:-1, label: 'Pending Request'},
    { value: 2, label: 'Saved' },
    { value: 3, label: 'Picked' },
    { value: 4, label: 'Completed' },
    { value: 5, label: 'Rejected' },
    { value: 6, label: 'Accepted' },
];

const PendingAds = ({ isActive }) => {
    const {
        fetchPendingAds,
        currentPage,
        setCurrentPage,
        setAdsData,
        adsData,
        hasMore,
        designers,
        fetchDesigners,
        loading,
        showActionsMenu,
        modal,
        drawer,
        expandedVersionsRow,
        fetchContentWriters,
        actionHandlers,
        renderCell,
        getModalAlt,
        closeExpandedRows,
        handleDesignerChange,
        handleContentWriterChange,
        contentWriters,
        fetchContent,
        salesList,
        getSalesList,
        fetchSalesLocations,
        salesLocations,
        totalRecords
    } = useTableState();
    // Filter state
    // const today = new Date().toISOString().split('T')[0];
    // const tomorrow = new Date();
    // tomorrow.setDate(tomorrow.getDate() + 1);
    // const tomorrowStr = tomorrow.toISOString().split('T')[0];
    const [dateRange, setDateRange] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.dateRange', { startDate: null, endDate: null });
    const [designer, setDesigner] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.designer', null);
    const [status, setStatus] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.status', null);
    const [adType, setAdType] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.adType', null);
    const [search, setSearch] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.search', '');
    const [salesPerson, setSalesPerson] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.salesPerson', null);
    const [salesLocation, setSalesLocation] = usePersistedFilterState('alpha_designer.my_ads.pending_ads.salesLocation', []);
    const [sortByLatestMessage, setSortByLatestMessage] = usePersistedFilterState('view_alpha_by_latest_message', false);

    // Designer options for react-select
    const designerOptions = useMemo(() =>
        designers.map(d => ({ value: d.designer_id, label: d.name })),
        [designers]
    );


    // Fetch designers on mount
    useEffect(() => {
        fetchDesigners();
        fetchContentWriters();
        getSalesList();
        fetchSalesLocations();
    }, []);

    const loadMoreCtx = useRef({});
    loadMoreCtx.current = { loading, hasMore, currentPage, isActive, dateRange, designer, status, adType, search, salesPerson, salesLocation, sortByLatestMessage };

    // Reset and fetch on filter/tab change
    useEffect(() => {
        setCurrentPage(0);
        setAdsData([]);
        fetchPendingAds(0, isActive, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            designerId: designer ? designer.value : null,
            status: status ? status.value : null,
            adType: adType && adType.value !== 'all' ? adType.value : null,
            search: search || null,
            sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
            sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
            sort_by_latest_message: sortByLatestMessage,
        }, false);
    }, [isActive, dateRange, designer, status, adType, search, salesPerson, salesLocation, sortByLatestMessage]);

    // On-demand re-fetch of the current filters/page, separate from the auto
    // fetch on filter/tab change or pagination.
    const handleRefresh = useCallback(() => {
        setCurrentPage(0);
        setAdsData([]);
        fetchPendingAds(0, isActive, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            designerId: designer ? designer.value : null,
            status: status ? status.value : null,
            adType: adType && adType.value !== 'all' ? adType.value : null,
            search: search || null,
            sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
            sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
            sort_by_latest_message: sortByLatestMessage,
        }, false);
    }, [fetchPendingAds, isActive, dateRange, designer, status, adType, search, salesPerson, salesLocation, sortByLatestMessage]);

    const handleLoadMore = useCallback(() => {
        const ctx = loadMoreCtx.current;
        if (ctx.loading || !ctx.hasMore) return;
        const nextPage = ctx.currentPage + 1;
        setCurrentPage(nextPage);
        fetchPendingAds(nextPage, ctx.isActive, {
            startDate: ctx.dateRange.startDate,
            endDate: ctx.dateRange.endDate,
            designerId: ctx.designer ? ctx.designer.value : null,
            status: ctx.status ? ctx.status.value : null,
            adType: ctx.adType && ctx.adType.value !== 'all' ? ctx.adType.value : null,
            search: ctx.search || null,
            sales_person_id: ctx.salesPerson?.value ? [ctx.salesPerson.value] : null,
            sales_location: ctx.salesLocation?.length ? ctx.salesLocation.map((o) => o.value) : null,
            sort_by_latest_message: ctx.sortByLatestMessage,
        }, true);
    }, [fetchPendingAds]);

    // Handlers
    const handleDateChange = (val) => setDateRange(val);
    const handleDesignerChangeSelect = (val) => setDesigner(val);
    const handleStatusChange = (val) => setStatus(val);
    const handleAdTypeChange = (val) => setAdType(val);
    const handleSearchChange = (value) => setSearch(value);
    const handleSalesPersonChange = (value) => setSalesPerson(value);
    const handleSalesLocationChange = (value) => setSalesLocation(value);
    const handleSortByLatestMessageChange = (value) => setSortByLatestMessage(value);

    return (
        <div>
            {/* Filter Bar */}
            <FilterBar
                dateRange={dateRange}
                onDateChange={handleDateChange}
                designer={designer}
                onDesignerChange={handleDesignerChangeSelect}
                designerOptions={designerOptions}
                status={status}
                onStatusChange={handleStatusChange}
                statusOptions={STATUS_OPTIONS}
                adType={adType}
                onAdTypeChange={handleAdTypeChange}
                adTypeOptions={types}
                search={search}
                onSearchChange={handleSearchChange}
                salesPerson={salesPerson}
                onSalesPersonChange={handleSalesPersonChange}
                salesPersonOptions={salesList}
                showSalesPerson={true}
                salesLocation={salesLocation}
                onSalesLocationChange={handleSalesLocationChange}
                showSalesLocation={true}
                salesLocationOptions={salesLocations}
                sortByLatestMessage={sortByLatestMessage}
                onSortByLatestMessageChange={handleSortByLatestMessageChange}
                showSortByLatestMessage={true}
            />
            <div className='flex flex-col items-start justify-center gap-2'>
                <div className="px-2 py-1 shadow-md rounded-lg bg-gray-300">
                    <span>Total Ads: {totalRecords}</span>
                </div>
                <AdTable
                    data={adsData}
                    columns={columnsConfig.pending_ads}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    isPending={true}
                    designers={designers}
                    contentWriters={contentWriters}
                    showActionsMenu={showActionsMenu}
                    modal={modal}
                    drawer={drawer}
                    expandedVersionsRow={expandedVersionsRow}
                    actionHandlers={actionHandlers}
                    renderCell={renderCell}
                    getModalAlt={getModalAlt}
                    closeExpandedRows={closeExpandedRows}
                    handleDesignerChange={(creativeId, designerId, designerName, creativeName) => handleDesignerChange(creativeId, designerId, designerName, creativeName, handleRefresh)}
                    handleContentWriterChange={(content_id, creativeId, contentWriterId, contentWriterName, description) => handleContentWriterChange(content_id, creativeId, contentWriterId, contentWriterName, description, handleRefresh)}
                    fetchContent={fetchContent}
                />
            </div>
        </div>
    );
};

export default PendingAds; 